import React from 'react'
import { Box, Typography } from '@mui/material'
import { formatTime } from '../../format'
import { advancedColors } from '../../theme'
import { TOUR_ANCHORS } from '../../tour/anchors'
import { useIsExampleRow, useIsTourRunning } from '../../tour/tourExample'
import RowActionsMenu, { RowAction } from './RowActionsMenu'

type Props = {
  side?: 'left' | 'right'
  timestamp?: string
  stickToTop?: boolean
  actions: RowAction[]
  tourAnchor?: string
  children: React.ReactNode
}

const noop = (): void => undefined

// Standard's `ItemWithSideInfo`: the time and the actions sit beside the item, and the actions show on hover.
const FeedRow = ({
  side = 'left',
  timestamp,
  stickToTop = false,
  actions,
  tourAnchor = TOUR_ANCHORS.feedRow,
  children,
}: Props) => {
  const direction = side === 'right' ? 'row-reverse' : 'row'
  const example = useIsExampleRow()
  const touring = useIsTourRunning()
  const inert = example || touring
  // The tour shows the menu but never runs an item, on an example row or a real one.
  const rowActions = inert ? actions.map((action) => ({ ...action, onClick: noop })) : actions

  return (
    <Box
      data-tour={tourAnchor}
      sx={{
        display: 'flex',
        flexDirection: direction,
        alignItems: stickToTop ? 'flex-start' : 'center',
        width: '100%',
        '&:hover .row-actions, & .row-actions:focus-within': { opacity: 1 },
      }}
    >
      {children}
      <Box
        sx={{
          display: 'flex',
          justifyContent: side === 'right' ? 'flex-end' : 'flex-start',
          minWidth: '25%',
          px: 2,
          pt: stickToTop ? 2.5 : 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: direction, alignItems: 'center', height: 0, gap: 1 }}>
          {timestamp && (
            <Typography variant="caption" sx={{ color: advancedColors.inkFaint, whiteSpace: 'nowrap' }}>
              {formatTime(timestamp)}
            </Typography>
          )}
          <Box className="row-actions" sx={{ opacity: 0 }}>
            <RowActionsMenu actions={rowActions} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default FeedRow
